import React from "react";
import { Sidebar } from "primereact/sidebar";
import "./DetailsSidebar.css";
import { Tag } from "primereact/tag";
import { InputTextarea } from "primereact/inputtextarea";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Node } from "reactflow";
import { NodeData } from "../types/types";
import { getCalculatedValue } from "../Helpers/Calculations";
import { useStore } from "../App/store";

interface DetailsSidebarProps {
  detailsSidebarVisible: boolean;
  setDetailsSidebarVisible: (visible: boolean) => void;
  selectedNode: Node<NodeData> | null;
}

const DetailsSidebar: React.FC<DetailsSidebarProps> = ({
  detailsSidebarVisible,
  setDetailsSidebarVisible,
  selectedNode,
}) => {
  // Function to strip HTML tags and convert to plain text
  const stripHtmlTags = (html: string): string => {
    if (!html) return "";

    // Create a temporary div element
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Get text content and clean up extra whitespace
    return temp.textContent || temp.innerText || "";
  };

  // Function to check if the icon is an emoji or an image URL
  const isEmoji = (str: string): boolean => {
    if (!str) return false;

    // Check if it's a URL (starts with http:// or https:// or data:)
    if (
      str.startsWith("http://") ||
      str.startsWith("https://") ||
      str.startsWith("data:")
    ) {
      return false;
    }

    // Check if the string length is very short (typical for emojis)
    // Most emojis are 1-4 characters in length
    if (str.length <= 10 && !/\.(jpg|jpeg|png|gif|svg|webp)/i.test(str)) {
      return true;
    }

    return false;
  };

  const plainTextNotes = stripHtmlTags(selectedNode?.data?.notes || "");
  const icon = selectedNode?.data?.icon || "";
  const isIconEmoji = isEmoji(icon);
  const task = selectedNode?.data?.task;
  const hasTask = task && task.id;
  const customProperties = selectedNode?.data?.customProperties || [];
  const hasCustomProperties = customProperties.length > 0;

  // Format date for display
  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return "Not set";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="card flex justify-content-center">
      <Sidebar
        showCloseIcon={false}
        className="details-sidebar"
        style={{
          paddingRight: "30px",
          display: "flex",
          flexDirection: "column",
        }}
        visible={detailsSidebarVisible}
        onHide={() => {
          setDetailsSidebarVisible(false);
        }}
        dismissable={false}
        modal={false}
        position="right"
        onContextMenu={(e) => e.stopPropagation()} // Prevent right-click bubbling
      >
        <div className="details-sidebar-content-wrapper">
          {/* Node Label Header */}
          <div className="details-sidebar-node-label">
            {selectedNode?.data?.label || "No node selected"}
          </div>

          {/* Notes Section - Wrapped in Accordion */}
          <Accordion activeIndex={0}>
            <AccordionTab header="Notes">
              <div className="details-sidebar-notes-section">
                <InputTextarea
                  value={plainTextNotes}
                  readOnly
                  rows={10}
                  autoResize={false}
                  className="details-sidebar-textarea"
                  placeholder="No notes available"
                />
              </div>
            </AccordionTab>
          </Accordion>

          {/* Task Section - Wrapped in Accordion */}
          {hasTask && (
            <Accordion activeIndex={null}>
              <AccordionTab header="Task">
                <div className="details-sidebar-task-section">
                  <div className="details-sidebar-task-item">
                    <span className="details-sidebar-task-label">Summary:</span>
                    <span className="details-sidebar-task-value">
                      {task.summary || "Not set"}
                    </span>
                  </div>
                  <div className="details-sidebar-task-item-row">
                    <span className="details-sidebar-task-label">Status:</span>
                    <span className="details-sidebar-task-value">
                      {task.status || "Not set"}
                    </span>
                  </div>
                  <div className="details-sidebar-task-item-row">
                    <span className="details-sidebar-task-label">Owner:</span>
                    <span className="details-sidebar-task-value">
                      {task.assignee || "Not set"}
                    </span>
                  </div>
                  <div className="details-sidebar-task-item-row">
                    <span className="details-sidebar-task-label">
                      Estimate:
                    </span>
                    <span className="details-sidebar-task-value">
                      {task.estimate !== undefined && task.estimate !== null
                        ? task.estimate
                        : "Not set"}
                    </span>
                  </div>
                  <div className="details-sidebar-task-item-row">
                    <span className="details-sidebar-task-label">
                      Start Date:
                    </span>
                    <span className="details-sidebar-task-value">
                      {formatDate(task.startDate)}
                    </span>
                  </div>
                  <div className="details-sidebar-task-item-row">
                    <span className="details-sidebar-task-label">
                      End Date:
                    </span>
                    <span className="details-sidebar-task-value">
                      {formatDate(task.endDate)}
                    </span>
                  </div>
                </div>
              </AccordionTab>
            </Accordion>
          )}

          {/* Properties Section - Wrapped in Accordion */}
          {hasCustomProperties && (
            <Accordion activeIndex={null}>
              <AccordionTab header="Properties">
                <div className="details-sidebar-properties-section">
                  {customProperties
                    .filter((prop) => prop.visible)
                    .map((prop, index) => {
                      let displayValue = "Not set";

                      if (prop.type === "calculated") {
                        const currency = useStore.getState().currency || "EUR";
                        const calculatedResult = getCalculatedValue(
                          prop,
                          customProperties,
                          currency
                        );
                        displayValue = calculatedResult?.value || "0";
                      } else if (prop.type === "date" && prop.value) {
                        displayValue = formatDate(prop.value);
                      } else if (prop.type === "boolean") {
                        displayValue = prop.value ? "Yes" : "No";
                      } else if (prop.value) {
                        displayValue = prop.value;
                      }

                      return (
                        <div
                          key={index}
                          className="details-sidebar-property-item-row"
                        >
                          <span className="details-sidebar-property-label">
                            {prop.name}:
                          </span>
                          <span className="details-sidebar-property-value">
                            {displayValue}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </AccordionTab>
            </Accordion>
          )}

          {/* Image Section - Wrapped in Accordion */}
          <Accordion activeIndex={null}>
            <AccordionTab header="Image">
              <div className="details-sidebar-image-section">
                {icon ? (
                  isIconEmoji ? (
                    <div className="details-sidebar-emoji">{icon}</div>
                  ) : (
                    <img
                      src={icon}
                      alt="Node"
                      className="details-sidebar-image"
                    />
                  )
                ) : (
                  <div className="details-sidebar-image-placeholder">
                    No image
                  </div>
                )}
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </Sidebar>
      <Tag
        className={`right-sidebar-toggle-button${
          detailsSidebarVisible ? " right-sidebar-open" : ""
        }`}
        value=""
        onClick={() => {
          setDetailsSidebarVisible(!detailsSidebarVisible);
        }}
        rounded
      >
        <i
          className="pi pi-info-circle"
          style={{ fontSize: "1.8em", marginRight: "0.6em", color: "#faf4e8" }}
        ></i>
      </Tag>
    </div>
  );
};

export default DetailsSidebar;
